// App.js
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LandingPage from './LandingPage';
import UploadPage from './Upload';
import Login from './Login';
import CombinedLegal from './CombinedLegal';
import DeleteAccount from './DeleteAccount';
import NotFound from './NotFound';
import RedirectPage from './RedirectPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DevModeProvider } from './context/DevModeContext';
import { initializeSmartBanner } from './smartBanner';
import { useEffect } from 'react';
import ErrorBoundary from './ErrorBoundary'; // Import the error boundary
import CircularProgress from '@mui/material/CircularProgress';

// Lazy load AdminPage to isolate any errors from affecting the rest of the app
const AdminPage = React.lazy(() => import('./AdminPage'));

// Admin-specific error fallback
function AdminErrorFallback() {
  return (
    <div style={{
      padding: '40px',
      textAlign: 'center',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <h2>Admin Panel Error</h2>
      <p>The admin panel encountered an error. The public site is unaffected.</p>
      <button
        onClick={() => window.location.href = '/'}
        style={{
          padding: '10px 20px',
          marginTop: '20px',
          cursor: 'pointer'
        }}
      >
        Return to Home
      </button>
    </div>
  );
}

// Public page error fallback
function PublicErrorFallback() {
  return (
    <div style={{
      padding: '40px',
      textAlign: 'center',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <h2>Oops! Something went wrong</h2>
      <p>We're sorry for the inconvenience. Please try refreshing the page.</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '10px 20px',
          marginTop: '20px',
          cursor: 'pointer'
        }}
      >
        Refresh Page
      </button>
    </div>
  );
}

// Wrapper to protect private routes and preserve 'from' location
// Updated wrapper with loading and token handling
function RequireAuth({ children }) {
  const location = useLocation();
  const { user, loading, initialized, token } = useAuth();

  if (loading || !initialized) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#000'
      }}>
        <CircularProgress />
      </div>
    );
  }

  if (!user || !token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Pass token to children via a wrapper if needed, or rely on useAuth in components
  return React.cloneElement(children, { authToken: token });
}

// Wrapper component for routing - public routes render immediately
function AppRoutes() {
  return (
    <Routes>
      {/* Public routes render immediately without waiting for auth */}
      <Route path="/" element={
        <ErrorBoundary fallback={<PublicErrorFallback />}>
          <LandingPage />
        </ErrorBoundary>
      } />
      <Route path="/login" element={
        <ErrorBoundary fallback={<PublicErrorFallback />}>
          <Login />
        </ErrorBoundary>
      } />
      <Route path="/legal-stuff" element={
        <ErrorBoundary fallback={<PublicErrorFallback />}>
          <CombinedLegal />
        </ErrorBoundary>
      } />
      <Route path="/delete-account" element={
        <ErrorBoundary fallback={<PublicErrorFallback />}>
          <DeleteAccount />
        </ErrorBoundary>
      } />
      <Route path="/app-link" element={
        <ErrorBoundary fallback={<PublicErrorFallback />}>
          <RedirectPage />
        </ErrorBoundary>
      } />
      {/* Admin routes wrapped in separate error boundary and lazy loaded */}
      <Route
        path="/noukakjypella"
        element={
          <ErrorBoundary fallback={<AdminErrorFallback />}>
            <Suspense fallback={
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#121212'
              }}>
                <CircularProgress />
              </div>
            }>
              <RequireAuth>
                <AdminPage />
              </RequireAuth>
            </Suspense>
          </ErrorBoundary>
        }
      />
      <Route
        path="/upload"
        element={
          <ErrorBoundary fallback={<PublicErrorFallback />}>
            <RequireAuth>
              <UploadPage />
            </RequireAuth>
          </ErrorBoundary>
        }
      />
      <Route path="*" element={
        <ErrorBoundary fallback={<PublicErrorFallback />}>
          <NotFound />
        </ErrorBoundary>
      } />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    initializeSmartBanner();
  }, []);
  return (
    <ErrorBoundary fallback={<PublicErrorFallback />}> {/* Added top-level boundary as a catch-all */}
      <DevModeProvider>
        <Router>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </Router>
      </DevModeProvider>
    </ErrorBoundary>
  );
}

export default App;